import React, { useState } from 'react';
import { Plus, Minus, Trash2, ShoppingBag, ArrowLeft } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../context/CartContext';
import { WHATSAPP_NUMBER } from '../data/products';

const emptyForm = { name: '', phone: '', address: '', city: '', pincode: '' };

export const CartDrawer = () => {
  const { items, updateQty, removeItem, subtotal, totalItems, isOpen, setIsOpen, clearCart } = useCart();
  const [step, setStep] = useState('cart');
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Required';
    if (!/^\d{10}$/.test(form.phone.trim())) errs.phone = 'Enter a valid 10-digit number';
    if (!form.address.trim()) errs.address = 'Required';
    if (!form.city.trim()) errs.city = 'Required';
    if (!/^\d{6}$/.test(form.pincode.trim())) errs.pincode = 'Enter a valid 6-digit PIN';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const placeOrder = () => {
    if (!validate()) return;
    const lines = items.map(
      (i, idx) => `${idx + 1}. ${i.name} (${i.weight}) x ${i.qty} — ₹${i.qty * i.price}`
    );
    const message =
      `*New Order — FurrBowl* 🐾\n\n` +
      `${lines.join('\n')}\n\n` +
      `*Subtotal: ₹${subtotal}*\n\n` +
      `*Deliver to:*\n` +
      `Name: ${form.name}\n` +
      `Phone: ${form.phone}\n` +
      `Address: ${form.address}\n` +
      `City: ${form.city} — PIN ${form.pincode}\n\n` +
      `Please confirm availability & total. Thank you!`;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    clearCart();
    setForm(emptyForm);
    setStep('cart');
    setIsOpen(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col p-0 bg-white"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-4 border-b border-gray-100">
          <SheetTitle className="flex items-center gap-2 text-[#1e1919]">
            {step === 'checkout' && (
              <button onClick={() => setStep('cart')} data-testid="back-to-cart" aria-label="Back to cart">
                <ArrowLeft size={20} />
              </button>
            )}
            <ShoppingBag size={20} className="text-[#ff8c19]" />
            {step === 'cart' ? `Your Cart (${totalItems})` : 'Delivery Details'}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
            <ShoppingBag size={48} className="text-[#736c64]/40" />
            <p className="text-[#736c64]">Your cart is empty.</p>
            <Button
              onClick={() => setIsOpen(false)}
              className="bg-[#61525a] hover:bg-[#524349] text-white rounded-full mt-2"
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {step === 'cart' ? (
                items.map((item) => (
                  <div key={item.id} className="flex gap-3 items-center" data-testid={`cart-item-${item.id}`}>
                    <img src={item.image} alt={item.name} className="h-16 w-16 rounded-xl object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1e1919] truncate">{item.name}</p>
                      <p className="text-xs text-[#736c64]">{item.weight} · ₹{item.price}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <button
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          className="h-7 w-7 flex items-center justify-center rounded-full bg-[#f7f5f2] hover:bg-[#e8e6e3]"
                          data-testid={`cart-decrease-${item.id}`}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="text-sm font-semibold w-5 text-center">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.id, item.qty + 1)}
                          className="h-7 w-7 flex items-center justify-center rounded-full bg-[#ff8c19] text-white hover:bg-[#e67d16]"
                          data-testid={`cart-increase-${item.id}`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <span className="text-sm font-bold text-[#61525a]">₹{item.qty * item.price}</span>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-[#736c64] hover:text-red-500"
                        data-testid={`cart-remove-${item.id}`}
                        aria-label="Remove item"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#1e1919]">Full Name</label>
                    <Input name="name" value={form.name} onChange={handleChange} placeholder="Your name" data-testid="checkout-name" className="mt-1" />
                    {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1e1919]">Phone (WhatsApp)</label>
                    <Input name="phone" value={form.phone} onChange={handleChange} placeholder="10-digit mobile number" data-testid="checkout-phone" className="mt-1" />
                    {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1e1919]">Delivery Address</label>
                    <Textarea name="address" value={form.address} onChange={handleChange} placeholder="House no, street, area, landmark" data-testid="checkout-address" className="mt-1" />
                    {errors.address && <p className="text-xs text-red-500 mt-1">{errors.address}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium text-[#1e1919]">City</label>
                      <Input name="city" value={form.city} onChange={handleChange} placeholder="City" data-testid="checkout-city" className="mt-1" />
                      {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#1e1919]">Pincode</label>
                      <Input name="pincode" value={form.pincode} onChange={handleChange} placeholder="6-digit PIN" data-testid="checkout-pincode" className="mt-1" />
                      {errors.pincode && <p className="text-xs text-red-500 mt-1">{errors.pincode}</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 px-6 py-4 space-y-3 bg-white">
              <div className="flex justify-between items-center">
                <span className="text-[#736c64]">Subtotal</span>
                <span className="text-xl font-bold text-[#1e1919]" data-testid="cart-subtotal">₹{subtotal}</span>
              </div>
              {step === 'cart' ? (
                <Button
                  onClick={() => setStep('checkout')}
                  className="w-full bg-[#ff8c19] hover:bg-[#e67d16] text-white rounded-full py-6 text-base font-semibold"
                  data-testid="proceed-checkout-btn"
                >
                  Proceed to Checkout
                </Button>
              ) : (
                <Button
                  onClick={placeOrder}
                  className="w-full bg-[#25D366] hover:bg-[#1da851] text-white rounded-full py-6 text-base font-semibold"
                  data-testid="place-order-whatsapp-btn"
                >
                  Place Order on WhatsApp
                </Button>
              )}
              <p className="text-xs text-center text-[#736c64]">
                You'll be redirected to WhatsApp to confirm your order & payment.
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
